import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../lib/constants";

export default function CookiePolicy() {
    return (
        <div className="min-h-screen bg-brand-cream bg-cream-grain">
            <header className="border-b border-brand-line bg-paper">
                <div className="mx-auto max-w-[900px] px-5 sm:px-8 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="cookie-home-link">
                        <img src={LOGO_URL} alt="RENOVA360" className="h-9 w-9 object-contain" />
                        <span className="font-display text-2xl text-brand-navy tracking-tight">RENOVA360</span>
                    </Link>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-navy transition-colors"
                        data-testid="cookie-back-link"
                    >
                        <ArrowLeft size={16} /> Torna al login
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-[820px] px-5 sm:px-8 py-12 lg:py-16" data-testid="cookie-page">
                <div className="eyebrow text-brand-gold mb-3">Documento legale</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                    Cookie Policy
                </h1>
                <p className="mt-3 text-sm text-brand-muted">Ultimo aggiornamento: 28 febbraio 2026</p>

                <div className="prose-renova mt-10 space-y-8 text-[15px] leading-7 text-brand-ink/90">
                    <Section title="1. Cosa sono i cookie">
                        <p>
                            I cookie sono piccoli file di testo che i siti web visitati inviano al dispositivo dell’utente,
                            dove vengono memorizzati per essere ritrasmessi al sito al successivo accesso. Possono essere
                            utilizzati per autenticazione, monitoraggio di sessioni, memorizzazione di preferenze o
                            informazioni statistiche.
                        </p>
                    </Section>

                    <Section title="2. Titolare del trattamento">
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><strong>MAKAI di Maria Laura Pellino</strong></li>
                            <li>Partita IVA: <strong>09936410969</strong></li>
                            <li>Email: <a href="mailto:info@renova-foodcost.shop" className="text-brand-navy underline">info@renova-foodcost.shop</a></li>
                        </ul>
                    </Section>

                    <Section title="3. Cookie utilizzati da RENOVA360">
                        <p>Il Servizio utilizza esclusivamente le seguenti tipologie di cookie e tecnologie di archiviazione locale:</p>

                        <div className="overflow-x-auto mt-4 border border-brand-line rounded-lg">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-brand-cream">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-brand-ink">Nome</th>
                                        <th className="px-4 py-3 font-semibold text-brand-ink">Tipologia</th>
                                        <th className="px-4 py-3 font-semibold text-brand-ink">Finalità</th>
                                        <th className="px-4 py-3 font-semibold text-brand-ink">Durata</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-brand-line">
                                    <tr>
                                        <td className="px-4 py-3"><code>token</code> (localStorage)</td>
                                        <td className="px-4 py-3">Tecnico essenziale</td>
                                        <td className="px-4 py-3">Autenticazione JWT dell’utente registrato.</td>
                                        <td className="px-4 py-3">30 giorni</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-3"><code>user</code> (localStorage)</td>
                                        <td className="px-4 py-3">Tecnico essenziale</td>
                                        <td className="px-4 py-3">Memorizzazione del profilo utente attivo per evitare ricaricamenti.</td>
                                        <td className="px-4 py-3">Fino a logout</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-3">Cookie Stripe (<code>__stripe_*</code>, <code>m</code>)</td>
                                        <td className="px-4 py-3">Terze parti — pagamenti</td>
                                        <td className="px-4 py-3">
                                            Impostati da Stripe sul dominio di checkout per prevenzione frodi e sicurezza dei pagamenti.
                                            Vedi <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noreferrer" className="text-brand-navy underline">Stripe Cookie Policy</a>.
                                        </td>
                                        <td className="px-4 py-3">Variabile (max 24 mesi)</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-3">Cookie Google OAuth</td>
                                        <td className="px-4 py-3">Terze parti — autenticazione</td>
                                        <td className="px-4 py-3">
                                            Impostati solo se l’utente sceglie il login con Google. Vedi
                                            {" "}<a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noreferrer" className="text-brand-navy underline">Google Cookie Policy</a>.
                                        </td>
                                        <td className="px-4 py-3">Sessione / 1 anno</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <p className="mt-4">
                            <strong>RENOVA360 non utilizza cookie di profilazione, marketing o analitici di terze parti.</strong>
                        </p>
                    </Section>

                    <Section title="4. Base giuridica">
                        <p>
                            I cookie tecnici essenziali (autenticazione, sicurezza, pagamento) non richiedono consenso ai sensi
                            dell’art. 122 del Codice Privacy, in quanto strettamente necessari all’erogazione del Servizio
                            espressamente richiesto dall’utente.
                        </p>
                    </Section>

                    <Section title="5. Come gestire i cookie">
                        <p>
                            L’utente può in qualsiasi momento eliminare o bloccare i cookie tramite le impostazioni del proprio
                            browser. La disattivazione dei cookie tecnici essenziali può tuttavia compromettere il funzionamento
                            del Servizio (per esempio impedendo l’autenticazione o il completamento del pagamento).
                        </p>
                        <p className="mt-2">
                            Istruzioni per i browser più diffusi:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer" className="text-brand-navy underline">Google Chrome</a></li>
                            <li><a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" target="_blank" rel="noreferrer" className="text-brand-navy underline">Mozilla Firefox</a></li>
                            <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noreferrer" className="text-brand-navy underline">Apple Safari</a></li>
                            <li><a href="https://support.microsoft.com/it-it/microsoft-edge" target="_blank" rel="noreferrer" className="text-brand-navy underline">Microsoft Edge</a></li>
                        </ul>
                    </Section>

                    <Section title="6. Modifiche">
                        <p>
                            La presente Cookie Policy potrà essere aggiornata; eventuali modifiche saranno pubblicate su questa
                            pagina con indicazione della data di ultimo aggiornamento.
                        </p>
                    </Section>

                    <div className="border-t border-brand-line pt-6 text-sm text-brand-muted">
                        Vedi anche la nostra <Link to="/privacy-policy" className="text-brand-navy underline" data-testid="cookie-to-privacy">Informativa sulla privacy</Link>.
                    </div>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-display text-2xl text-brand-navy mb-2">{title}</h2>
            <div>{children}</div>
        </section>
    );
}
